<template>
    <div>
        <div class="form-group">
            <label class="control-label col-sm-4">{{ $t('cost.supplier') }}</label>
            <div class="col-sm-8">
                <input
                    v-model="filter"
                    type="text"
                    class="form-control mb-2"
                    placeholder="Search"
                >

                <input
                    :value="selectedSupplierId"
                    type="hidden"
                    name="account_id"
                >

                <template v-if="filteredSuppliers.length">
                    <div
                        v-for="supplier in filteredSuppliers"
                        :key="`supplier-radio-${supplier.id}`"
                        class="radio"
                    >
                        <label>
                            <input
                                v-model="selectedSupplierId"
                                type="radio"
                                name="supplier_account_id"
                                :value="supplier.id"
                                class="simple"
                            > {{ supplier.name }}
                        </label>
                    </div>
                </template>
                <div
                    v-else
                    class="alert alert-warning"
                >
                    <span v-if="filteredSuppliers.length !== suppliers.length">No results</span>
                    <span v-else-if="suppliers.length === 0">You have not assigned any suppliers to costs.</span>
                </div>
            </div>
        </div>
        
        <div
            v-if="!selectedSupplier"
            class="form-group"
        >
            <div class="col-sm-8 offset-sm-4">
                <div class="alert alert-warning">
                    Please select a {{ $t('cost.supplier') }}
                </div>
            </div>
        </div>

        <div
            v-if="costs.length"
            class="form-group"
        >
            <label class="control-label col-sm-4">{{ $t('cost.costs') }}</label>
            <div class="col-sm-8">
                <table
                    v-if="selectedSupplier"
                    class="table"
                >
                    <thead>
                        <tr>
                            <th>
                                <div class="checkbox no-padding">
                                    <label>
                                        <input
                                            v-model="selectAllCosts"
                                            type="checkbox"
                                            class="simple"
                                        >
                                    </label>
                                </div>
                            </th>
                            <th>{{ $t('cost.type') }}</th>
                            <th>{{ $t('cost.description') }}</th>
                            <th class="number-cell">
                                {{ $t('cost.quantity') }}
                            </th>
                            <th class="number-cell">
                                {{ $t('cost.unit_cost') }}
                            </th>
                            <th class="number-cell">
                                {{ $t('cost.total_cost') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="cost in costs"
                            :key="`cost-row-${cost.id}`"
                        >
                            <td>
                                <div class="checkbox">
                                    <label>
                                        <input
                                            v-model="selectedCosts"
                                            type="checkbox"
                                            :value="cost.id"
                                            :name="`costs[${selectedSupplier.id}][]`"
                                            class="simple"
                                        >
                                    </label>
                                </div>
                            </td>
                            <td>{{ cost.type }}</td>
                            <td v-html="cost.description_html" />
                            <td class="number-cell">
                                {{ cost.quantity }}
                            </td>
                            <td class="number-cell">
                                {{ cost.unit_cost }}
                            </td>
                            <td class="number-cell">
                                {{ cost.total_cost }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        
        <div
            v-if="allocations.length"
            class="form-group"
        >
            <label class="control-label col-sm-4">{{ $t('resource.resources') }}</label>
            <div class="col-sm-8">
                <table
                    v-if="selectedSupplier"
                    class="table"
                >
                    <thead>
                        <tr>
                            <th>
                                <div class="checkbox no-padding">
                                    <label>
                                        <input
                                            v-model="selectAllAllocations"
                                            type="checkbox"
                                            class="simple"
                                        >
                                    </label>
                                </div>
                            </th>
                            <th>{{ $t('allocation.resource_type_name') }}</th>
                            <th>{{ $t('allocation.resource_name') }}</th>
                            <th class="number-cell">
                                {{ $t('allocation.quantity') }}
                            </th>
                            <th class="number-cell">
                                {{ $t('allocation.unit_cost') }}
                            </th>
                            <th class="number-cell">
                                {{ $t('allocation.total_cost') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="allocation in allocations"
                            :key="`allocation-row-${allocation.id}`"
                        >
                            <td>
                                <div class="checkbox">
                                    <label>
                                        <input
                                            v-model="selectedAllocations"
                                            type="checkbox"
                                            :value="allocation.id"
                                            :name="`allocations[${selectedSupplier.id}][]`"
                                            class="simple"
                                        >
                                    </label>
                                </div>
                            </td>
                            <td>{{ allocation.resource_type }}</td>
                            <td>{{ allocation.resource_name }}</td>
                            <td class="number-cell">
                                {{ allocation.quantity }}
                            </td>
                            <td class="number-cell">
                                {{ allocation.unit_cost }}
                            </td>
                            <td class="number-cell">
                                {{ allocation.total_cost }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

</template>

<script>
import client from '@/api';

export default {
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            suppliers: [],
            selectedSupplierId: null,
            selectedCosts: [],
            selectedAllocations: [],
            filter: null,
        };
    },
    computed: {
        costs() {
            if (this.selectedSupplier) {
                return this.selectedSupplier.costs;
            }

            return [];
        },
        allocations() {
            if (this.selectedSupplier) {
                return this.selectedSupplier.allocations;
            }
            
            return [];
        },
        selectedSupplier() {
            return this.suppliers.find(supplier => supplier.id === this.selectedSupplierId);
        },
        selectAllCosts: {
            get() {
                return this.selectedCosts.length > 0 && this.selectedCosts.length === this.selectedSupplier.costs.length;
            },
            set(value) {
                if (value) {
                    this.selectedCosts = this.selectedSupplier.costs.map(cost => cost.id);
                } else {
                    this.selectedCosts = [];
                }
            },
        },
        selectAllAllocations: {
            get() {
                return this.selectedAllocations.length > 0 && this.selectedAllocations.length === this.selectedSupplier.allocations.length;
            },
            set(value) {
                if (value) {
                    this.selectedAllocations = this.selectedSupplier.allocations.map(allocation => allocation.id);
                } else {
                    this.selectedAllocations = [];
                }
            },
        },
        filteredSuppliers() {
            if (this.filter) {
                return this.suppliers.filter(supplier => supplier.name.toLowerCase().indexOf(this.filter.toLowerCase()) !== -1);
            }

            return this.suppliers;
        },
    },
    mounted() {
        client.execute('get', `events/${this.id}/suppliers`)
            .then((data) => {
                this.suppliers = data;
            });
    },
};
</script>

<style scoped>

</style>
